<template>
  <div class="container-main">
    <div
      class="container-main__newsfeed newsfeed"
      :style="{ width: state.sumWidth + 'px' }"
    >
      <NewsPageToolbar
        v-if="isToolbarActive && !isShowFullNews"
        :chosen-card-list="chosenCardList"
        :chosen-detail-card-list="chosenDetailCardList"
        :is-all-cards-chosen="isAllCardsChosen"
        :is-all-page-card-chosen="isAllPageCardChosen"
        :chosen-rubric-ids="chosenRubricIds"
        :excluded-cards-ids="excludedCardsIds"
        :excluded-detail-card-list="excludedDetailCardList"
        @reset-card-list="resetCardList"
        @toggle-all-cards="emit('toggle-all-cards', $event)"
        @select-all-cards="emit('select-all-cards')"
      />

      <div ref="newsFeed" class="newsfeed__wrapper scroll">
        <NewsPageBodyNewsfeedItem
          v-for="(post, key) in newsFeedStore.items || []"
          :key="post.id"
          :box-width="state.width"
          :data="post"
          :index="key"
          :is-toolbar-active="isToolbarActive"
          :chosen-card-list="chosenCardList"
          @update:chosen-card-list="
            (cards) => emit('update:chosen-card-list', cards)
          "
        />
      </div>

      <div
        v-if="newsFeedStore.loading && newsFeedStore.totalCount >= 1"
        class="container-main__newsfeed--loader"
      />

      <NewsPagePagination
        v-if="newsFeedStore.totalCount >= 1"
        :container="newsFeed"
      />
    </div>

    <div
      v-if="newsFeedStore.items?.length"
      class="splitter"
      @mousedown="startDrag"
    />
  </div>
</template>

<script setup>
import NewsPageBodyNewsfeedItem from "@/components/NewsPage/NewsPageBody/NewsPageBodyNewsfeedItem.vue";
import { useNewsFeedStore } from "@/store/NewsFeed/newsFeed.js";
import { reactive, ref, onMounted, defineProps, defineEmits } from "vue";
import NewsPagePagination from "@/components/NewsPage/NewsPageRoof/NewsPagePagination.vue";
import NewsPageToolbar from "@/components/NewsPage/NewsPageToolbar.vue";

const emit = defineEmits([
  "update:chosen-card-list",
  "toggle-all-cards",
  "toggle-all-cards-global",
  "select-all-cards",
  "reset-card-list",
]);

const props = defineProps({
  isToolbarActive: {
    type: Boolean,
    required: true,
    default: false,
  },
  isShowFullNews: {
    type: Boolean,
    default: false,
  },
  chosenCardList: {
    type: Array,
    required: true,
    default: () => [],
  },
  chosenDetailCardList: {
    type: Array,
    default: () => [],
  },
  isAllPageCardChosen: {
    type: Boolean,
    default: false,
  },
  isAllCardsChosen: {
    type: Boolean,
    default: false,
  },
  chosenRubricIds: {
    type: Array,
    default: () => [],
  },
  excludedCardsIds: {
    type: Array,
    default: () => [],
  },
  excludedDetailCardList: {
    type: Array,
    default: () => [],
  },
  parentContainerWidth: {
    type: Number,
    default: null,
  },
});

const state = reactive({
  width: 471,
  mouseStart: 0,
  mouseDiff: 0,
  sumWidth: 471,
});

const newsFeedStore = useNewsFeedStore();
const newsFeed = ref(null);

function throttle(func, limit) {
  let lastFunc;
  let lastRan;

  return function (...args) {
    if (!lastRan) {
      func.apply(this, args);
      lastRan = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(function () {
        if (Date.now() - lastRan >= limit) {
          func.apply(this, args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
}

const handleMouseMove = throttle((e) => {
  state.mouseDiff = e.clientX - state.mouseStart;
  const newWidth = state.width + state.mouseDiff;
  const maxWidth = (props.parentContainerWidth - 260) * 0.5; // 260px - ширина aside и отступы
  state.sumWidth =
    newWidth < 450 ? 450 : newWidth > maxWidth ? maxWidth : newWidth;
}, 22); // Throttle to 22ms

function startDrag(e) {
  state.mouseStart = e.clientX;
  document.addEventListener("mousemove", handleMouseMove);
  window.addEventListener("mouseup", stopDrag);
  newsFeedStore.pointer = true;
}

function stopDrag() {
  const maxWidth = (props.parentContainerWidth - 260) * 0.5;
  state.width =
    state.sumWidth < 450
      ? 450
      : state.sumWidth > maxWidth
      ? maxWidth
      : state.sumWidth;
  document.removeEventListener("mousemove", handleMouseMove);
  window.removeEventListener("mouseup", stopDrag);

  newsFeedStore.pointer = false;
  localStorage.setItem("widthContainer", state.width);
}

const resetCardList = () => {
  emit("reset-card-list");
};

onMounted(() => {
  if (localStorage.getItem("widthContainer")) {
    state.sumWidth = Number(localStorage.getItem("widthContainer"));
    state.width = Number(localStorage.getItem("widthContainer"));
  }
});
</script>

<style lang="scss" scoped>
.container-main {
  display: flex;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  position: relative;

  .splitter {
    cursor: col-resize;
    height: 100%;
    width: 10px;
    position: absolute;
    top: 0;
    right: -10px;
  }

  &__newsfeed {
    position: relative;
    display: flex;
    flex: 1;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-width: 450px;
    min-height: 0;
    max-width: 100%;
    overflow: hidden;

    &--loader {
      top: 32px;
      right: 10px;
      position: absolute;
      width: calc(100% - 10px);
      height: 100%;
      background-color: rgba(255, 255, 255, 1);
      opacity: 40%;
    }
  }
}

.newsfeed {
  &__wrapper {
    position: relative;
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 5px;
    min-height: 0;
    padding: 0 5px 11px 0;
    overflow-x: hidden;
    overflow-y: auto;
  }

  &__item {
    &:not(:last-child) {
      margin: 0 0 5px 0;
    }
  }

  &__loader {
    display: flex;
    justify-content: center;
    padding: 1.389vw 0;
    align-items: center;
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(100px, -50%);
  }
}
</style>
